import { useState } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Table,
  Loader,
  Center,
  Checkbox,
  SimpleGrid,
  ScrollArea,
  Box,
  Alert,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircle } from "@tabler/icons-react";
import { useGroupsAdmin } from "../../../hooks/admin/useGroups";
import {
  useGroupTasksByGroup,
  useSetGroupTasks,
} from "../../../hooks/admin/useGroupTasks";
import { useTasksAdmin } from "../../../hooks/admin/useTasks";
import { Group as GroupType } from "../../../services/admin/groups.service";

function EditGroupTasksModal({
  group,
  opened,
  onClose,
}: {
  group: GroupType;
  opened: boolean;
  onClose: () => void;
}) {
  const { data: tasks = [], isLoading: tasksLoading } = useTasksAdmin();
  const { data: groupTasksData, isLoading: groupTasksLoading } =
    useGroupTasksByGroup(group.id);
  const setGroupTasks = useSetGroupTasks();

  const activeTasks = tasks.filter((t) => t.isActive);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  if (!groupTasksLoading && !initialized) {
    setDraft(new Set((groupTasksData?.tasks || []).map((t) => t.id)));
    setInitialized(true);
  }

  const toggle = (id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    setGroupTasks.mutate(
      { groupId: group.id, taskIds: Array.from(draft) },
      { onSuccess: onClose }
    );
  };

  const isLoading = tasksLoading || groupTasksLoading;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Tasks — ${group.name}`}
      size="xl"
      closeOnClickOutside={false}
      styles={{ body: { padding: 0 } }}
    >
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : (
        <>
          <ScrollArea.Autosize
            mah="calc(100vh - 300px)"
            offsetScrollbars
            px="md"
            pt="md"
          >
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Select the tasks this group grants to its members. Groups can
                have zero tasks (no minimum requirement).
              </Text>
              <SimpleGrid cols={3}>
                {activeTasks.map((task) => (
                  <Checkbox
                    key={task.id}
                    label={task.name}
                    checked={draft.has(task.id)}
                    onChange={() => toggle(task.id)}
                    color="gcpBlue"
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </ScrollArea.Autosize>

          <Box
            px="md"
            py="sm"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="space-between">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                color="gcpBlue"
                onClick={handleSave}
                loading={setGroupTasks.isPending}
              >
                Save
              </Button>
            </Group>
          </Box>
        </>
      )}
    </Modal>
  );
}

export default function GroupTasksAdminPage() {
  const { data: groups = [], isLoading } = useGroupsAdmin();
  const activeGroups = groups.filter((g) => g.isActive);

  const [editingGroup, setEditingGroup] = useState<GroupType | null>(null);
  const [editOpened, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);

  const handleEdit = (group: GroupType) => {
    setEditingGroup(group);
    openEdit();
  };

  const handleClose = () => {
    closeEdit();
    setEditingGroup(null);
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        <div>
          <Title order={2}>Group Tasks</Title>
          <Text c="dimmed" size="sm">
            Configure which tasks each group grants to its members. A user's
            effective tasks are the union of their role tasks and all group
            tasks.
          </Text>
        </div>

        <Alert
          icon={<IconAlertCircle size={16} />}
          color="blue"
          variant="light"
        >
          Changes take effect on each user's next login or task selection.
        </Alert>

        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Group</Table.Th>
              <Table.Th>Assigned Tasks</Table.Th>
              <Table.Th w={100}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {activeGroups.map((group) => (
              <GroupTaskRow
                key={group.id}
                group={group}
                onEdit={() => handleEdit(group)}
              />
            ))}
            {activeGroups.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={3}>
                  <Text c="dimmed" size="sm" ta="center" py="md">
                    No active groups found.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Stack>

      {editingGroup && (
        <EditGroupTasksModal
          group={editingGroup}
          opened={editOpened}
          onClose={handleClose}
        />
      )}
    </Container>
  );
}

function GroupTaskRow({
  group,
  onEdit,
}: {
  group: GroupType;
  onEdit: () => void;
}) {
  const { data } = useGroupTasksByGroup(group.id);
  const tasks = data?.tasks || [];

  return (
    <Table.Tr>
      <Table.Td fw={500}>{group.name}</Table.Td>
      <Table.Td>
        <Group gap={4} wrap="wrap">
          {tasks.length === 0 ? (
            <Text size="sm" c="dimmed">
              No tasks assigned
            </Text>
          ) : (
            tasks.map((t) => (
              <Badge key={t.id} variant="light" color="teal" size="sm">
                {t.name}
              </Badge>
            ))
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Button size="xs" variant="light" onClick={onEdit}>
          Edit
        </Button>
      </Table.Td>
    </Table.Tr>
  );
}
